import React, { useState, useEffect } from 'react';
import { useMutation }   from '@tanstack/react-query';
import Button            from '../components/ui/Button';
import Input             from '../components/ui/Input';
import Icon              from '../components/ui/Icon';
import { useAuth }       from '../context/AuthContext';
import { useToast }      from '../context/ToastContext';
import { setupPropertyApi } from '../api/property';
import { useEnabledLocations } from '../hooks/useLocations';

const PROPERTY_TYPES = [
  { label: 'Airbnb',       value: 'airbnb'       },
  { label: 'Private Home', value: 'private_home' },
];

const EMPTY_FORM = {
  property_name:    '',
  property_type:    'airbnb',
  property_link:    '',
  property_address: '',
  address_second:   '',
  city:             '',
  state:            '',
  zip_code:         '',
  country:          '',
};

const validate = (f) => {
  const e = {};
  if (!f.property_name.trim())    e.property_name    = 'Property name is required';
  if (f.property_link.trim() && !/^https?:\/\/.+/.test(f.property_link.trim()))
    e.property_link = 'Enter a valid URL (https://...)';
  if (!f.property_address.trim()) e.property_address = 'Address is required';
  if (!f.city.trim())             e.city             = 'City is required';
  if (!f.state.trim())            e.state            = 'State is required';
  if (!f.country.trim())          e.country          = 'Country is required';
  return e;
};

const AddPropertyPage = () => {
  const { onPropertyAdded, setupToken } = useAuth();
  const { error: toastError } = useToast();
  const { data: countries = [], isLoading: locationsLoading } = useEnabledLocations();

  const createMutation = useMutation({
    mutationFn: (payload) => setupPropertyApi(payload, setupToken),
    onSuccess:  () => onPropertyAdded(),
    onError:    (e) => toastError(e?.message || 'Failed to add property.'),
  });

  const [form,   setForm]   = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (countries.length === 1)
      setForm((f) => f.country ? f : { ...f, country: countries[0].country_name });
  }, [countries]);

  const set = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
  };

  const handleSubmit = () => {
    const e = validate(form);
    if (Object.keys(e).length) { setErrors(e); return; }
    createMutation.mutate(form);
  };

  return (
    <div className="page-scroll flex flex-col min-h-full bg-surface-900 relative overflow-hidden">
      <div className="noise-overlay" />
      <div
        className="absolute -top-16 -right-16 w-56 h-56 rounded-full opacity-[0.07] pointer-events-none"
        style={{ background: 'radial-gradient(circle, #6366f1, transparent 70%)' }}
      />

      {/* Header */}
      <div className="px-6 pt-14 pb-4 animate-fadeUp" style={{ animationFillMode: 'forwards' }}>
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 brand-glow"
          style={{ background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 60%, #818cf8 100%)' }}
        >
          <Icon name="building" size={24} className="text-white" />
        </div>
        <p className="font-dm text-surface-200 text-xs uppercase tracking-widest mb-1">Almost there</p>
        <h1 className="font-syne font-extrabold text-slate-800 text-[1.8rem] leading-tight">
          Add your<br /><span className="text-gradient">property</span>
        </h1>
        <p className="font-dm text-surface-200 text-sm mt-2">
          Tell us about your property to complete setup.
        </p>
      </div>

      {/* Form */}
      <div className="flex-1 px-5 pb-10 animate-fadeUp s1" style={{ animationFillMode: 'forwards', opacity: 0 }}>
        <div className="glass rounded-3xl p-5 card-shadow flex flex-col gap-4">

          <Input
            label="Property Name"
            name="property_name"
            placeholder="e.g. Beachside Villa"
            value={form.property_name}
            onChange={set('property_name')}
            error={errors.property_name}
            icon="building"
          />

          {/* Property Type */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-dm font-medium text-surface-100 uppercase tracking-wider">
              Property Type
            </label>
            <div className="flex flex-wrap gap-2">
              {PROPERTY_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, property_type: t.value }))}
                  className={`px-3 py-1.5 rounded-xl text-xs font-dm font-semibold border transition-all press-scale ${
                    form.property_type === t.value
                      ? 'bg-brand-600 border-brand-600 text-white'
                      : 'bg-surface-700 border-surface-400 text-surface-100 hover:border-brand-500'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <Input
            label="Property Link (optional)"
            name="property_link"
            placeholder="https://www.airbnb.com/rooms/12345678"
            value={form.property_link}
            onChange={set('property_link')}
            error={errors.property_link}
            icon="link"
          />

          <Input
            label="Property Address"
            name="property_address"
            placeholder="123 Palm Street"
            value={form.property_address}
            onChange={set('property_address')}
            error={errors.property_address}
            icon="location"
          />

          <Input
            label="Address Line 2 (optional)"
            name="address_second"
            placeholder="Floor 2, Unit 4B…"
            value={form.address_second}
            onChange={set('address_second')}
          />

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-dm font-medium text-surface-100 uppercase tracking-wider">
              Country
            </label>
            <select
              value={form.country}
              onChange={set('country')}
              disabled={locationsLoading}
              className={`h-[52px] w-full rounded-2xl bg-surface-600 border text-sm font-dm text-slate-800 px-4 transition-all duration-200 focus:outline-none disabled:opacity-50 ${
                errors.country
                  ? 'border-rose-400 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/15'
                  : 'border-surface-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/15'
              }`}
            >
              <option value="">Select country</option>
              {countries.map((c) => (
                <option key={c.country_code} value={c.country_name}>
                  {c.country_name}
                </option>
              ))}
            </select>
            {errors.country && (
              <p className="text-xs text-rose-500 font-dm flex items-center gap-1">
                <span>⚠</span> {errors.country}
              </p>
            )}
          </div>

          <div className="flex gap-3">
            <Input
              label="State"
              name="state"
              placeholder="e.g. Maharashtra"
              value={form.state}
              onChange={set('state')}
              error={errors.state}
              className="flex-1"
            />
            <Input
              label="City"
              name="city"
              placeholder="e.g. Mumbai"
              value={form.city}
              onChange={set('city')}
              error={errors.city}
              className="flex-1"
            />
          </div>

          <Input
            label="ZIP Code"
            name="zip_code"
            placeholder="e.g. 400001"
            value={form.zip_code}
            onChange={set('zip_code')}
          />
        </div>

        <div className="mt-4 animate-fadeUp s2" style={{ animationFillMode: 'forwards', opacity: 0 }}>
          <Button size="lg" loading={createMutation.isPending} onClick={handleSubmit}>
            <Icon name="check" size={16} />
            Submit Property
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddPropertyPage;
